import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  ConstructionBottomTabInfo,
  WiredConstructionBottomTabs,
} from '@euler/app/tv/components/construction/BottomTabs';
import { JobPageView } from '@euler/app/tv/components/construction/JobPageView';
import { ConstructionOverview } from '@euler/app/tv/components/construction/Overview';
import { TaskScreenContent } from '@euler/app/tv/components/TaskScreenContent';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ConstructionJob } from '@euler/model/task-detail';
import { useCallback, useEffect, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export const ConstructionScreen = wrapNavigatorScreen(
  ({ jobId }: { jobId?: number }) => {
    const [marginLeft, marginRight, insets] = useScreenContentPadding();
    const { detail, inventoryQuery } = useTaskContext();
    const { width } = useWindowDimensions();
    const { rpx } = useResponsiveContext();
    const [initialJobIndex] = useState(() => {
      if (jobId == null) return undefined;
      const index = detail.construction.jobs.findIndex(x => x.id === jobId);
      return index >= 0 ? index : undefined;
    });
    const [activeIndex, setActiveIndex] = useState(
      initialJobIndex != null ? initialJobIndex + 1 : 0,
    );
    const translateX = useSharedValue(-activeIndex * width);
    const [activeTab, setActiveTab] = useState<ConstructionBottomTabInfo>(
      initialJobIndex != null
        ? {
            type: 'job',
            job: detail.construction.jobs[initialJobIndex],
          }
        : { type: 'overview' },
    );
    const count = detail.construction.jobs.length;

    const onTabPress = useCallback(
      (tabInfo: ConstructionBottomTabInfo) => {
        const index =
          tabInfo.type === 'overview'
            ? 0
            : 1 +
              detail.construction.jobs.findIndex(x => x.id === tabInfo.job.id);
        if (index === activeIndex) return;
        setActiveIndex(index);
        setActiveTab(tabInfo);
      },
      [activeIndex, detail.construction.jobs],
    );

    const onConstructionJobPress = useCallback(
      (job: ConstructionJob) => {
        const index =
          1 + detail.construction.jobs.findIndex(x => x.id === job.id);
        if (index === activeIndex) return;
        setActiveIndex(index);
        setActiveTab({ type: 'job', job });
      },
      [activeIndex, detail.construction.jobs],
    );

    useEffect(() => {
      translateX.value = withTiming(-activeIndex * width, { duration: 250 });
    }, [activeIndex, translateX, width]);

    const animatedStyle = useAnimatedStyle(() => {
      return { transform: [{ translateX: translateX.value }] };
    });

    return (
      <TaskScreenContent
        targetStatus="construction"
        css={`
          padding-bottom: 0;
        `}
      >
        <LayoutProviderView
          css={`
            flex: 1;
            overflow: hidden;
          `}
        >
          <Animated.View
            css={`
              flex: 1;
              flex-direction: row;
              align-items: stretch;
              flex-wrap: nowrap;
              width: ${count * width}px;
            `}
            style={animatedStyle}
          >
            <ConstructionOverview
              detail={detail}
              inventoryQuery={inventoryQuery}
              onConstructionJobPress={onConstructionJobPress}
              css={`
                width: ${width}px;
              `}
            />
            {detail.construction.jobs.map(job => (
              <JobPageView
                key={job.id}
                job={job}
                width={width}
                active={activeTab.type === 'job' && activeTab.job.id === job.id}
              />
            ))}
          </Animated.View>
        </LayoutProviderView>

        <WiredConstructionBottomTabs
          detail={detail}
          activeTab={activeTab}
          onTabPress={onTabPress}
          css={`
            margin-left: ${marginLeft}px;
            margin-right: ${marginRight}px;
            height: ${rpx(148) + insets.bottom / 2}px;
            padding-bottom: ${insets.bottom / 2}px;
          `}
        />
      </TaskScreenContent>
    );
  },
);
